import React from 'react';
import styled, { css, DefaultTheme } from 'styled-components';
import { Link, LinkProps } from 'react-router-dom';
import { Intent } from '../utilities';
import { ButtonCss } from '../Button';

export type NavigationAppearance = 'primary' | 'default' | 'minimal';
export const NavigationAppearanceOptions: NavigationAppearance[] = ['primary', 'default', 'minimal'];

export interface INavigationButtonProps {
  /* Use Styling to Indicate that this is an active link */
  isActive?: boolean;
  /* Use a green underline (border) when active */
  showUnderline?: boolean;
  /* In addition to styling it as a link, make it also inline */
  isInline?: boolean;
  /* When a link is considered Primary, that means that it should be bold */
  isPrimary?: boolean;
  /* Buttons have the unique properties of appearance and intent */
  appearance?: NavigationAppearance;
  intent?: Intent;
  /** the navigation item will inherit colors from its parent*/
  inheritParentColors?: boolean;
  /** Use Spruce color scheme instead of default theme colors */
  $useSpruceColors?: boolean;
  disabled?: boolean;
}

const isButton = (props: INavigationButtonProps): boolean => {
  /* the properties appearance and intent are unique to the Button component */
  return !!props.appearance && !!props.intent;
};

const intentToColor = (intent: Intent | undefined, theme: DefaultTheme, $useSpruceColors?: boolean): string => {
  /* if intentToColor is called, then it's after isButton.
   isButton tests whether props.intent is defined.
   but, because intent is an optional field of INavigationButtonProps,
   TS requires a case when intent is undefined.
   */
  switch (intent) {
    case 'secondary':
      return theme.colors.blue;
    case 'danger':
      return theme.colors.error;
    default:
      return $useSpruceColors ? theme.colors.spruceDarkGreen : theme.colors.primary;
  }
};

const hoverColor = (props: INavigationButtonProps, theme: DefaultTheme): string => {
  if (props.inheritParentColors) return 'inherit';
  if (isButton(props)) {
    if (props.appearance === 'primary') {
      return theme.colors.white;
    }
    return intentToColor(props.intent, theme, props.$useSpruceColors);
  }
  return props.$useSpruceColors ? theme.colors.spruceDarkGreen : theme.colors.primaryDark;
};

const hoverBorder = (props: INavigationButtonProps, theme: DefaultTheme): string => {
  if (isButton(props)) {
    return 'none';
  } else if (props.showUnderline && props.isActive) {
    return props.$useSpruceColors ? theme.colors.spruceDarkGreen : theme.colors.primaryDark;
  }
  return 'transparent';
};

const hoverTextDecoration = (props: INavigationButtonProps): string => {
  if (isButton(props) && (!props.isInline || props.appearance !== 'minimal')) {
    // an inline minimal NavigationButton is virtually identical to an inline NavigationItem.
    return 'none';
  }
  return 'underline';
};

const focusBackground = (props: INavigationButtonProps, theme: DefaultTheme): string => {
  if (isButton(props) && props.appearance !== 'minimal') {
    return intentToColor(props.intent, theme, props.$useSpruceColors);
  }
  return 'transparent';
};

const focusBorderBottom = (props: INavigationButtonProps, theme: DefaultTheme): string => {
  if (isButton(props)) {
    return 'none';
  }
  const primaryColor = props.$useSpruceColors ? theme.colors.spruceDarkGreen : theme.colors.primary;
  return `2px solid ${props.showUnderline && props.isActive ? primaryColor : 'transparent'}`;
};

const sharedStyle = css<INavigationButtonProps>`
  display:${(props) => (props.isInline ? 'inline-block' : 'flex')};
  align-items:center;
  ${(props) => (props.isInline ? 'text-align:left;' : '')};
  cursor:pointer;
  background-color:transparent;
  ${(props) => (props.isInline ? 'font-size:inherit;font-family:inherit' : props.theme.fonts.mixins.smallButton)};
  font-weight:${(props) => (props.isPrimary ? 'bold' : 'normal')};
  text-decoration:none;
  
  padding: ${(props) => (props.isInline ? '0' : props.theme.spacing.spacing4)};
  color: ${(props) =>
    props.inheritParentColors
      ? 'inherit'
      : props.isActive || props.isInline
        ? (props.$useSpruceColors ? props.theme.colors.spruceDarkGreen : props.theme.colors.primaryDark)
        : props.theme.colors.darkGray};
  border:none;
  border-radius: unset;
  border-bottom: 2px solid ${(props) =>
    props.showUnderline && props.isActive 
      ? (props.$useSpruceColors ? props.theme.colors.spruceDarkGreen : props.theme.colors.primaryDark) 
      : 'transparent'};
  ${(props) => (props.isInline ? 'margin-bottom:-2px;' : '')};
  min-width:0;
  text-transform:none;
  :hover {
    color:${(props) => hoverColor(props, props.theme)};
    text-decoration:${(props) => hoverTextDecoration(props)};
    background-color:transparent;
    border-bottom: 2px solid ${(props) => hoverBorder(props, props.theme)};
  }
  :focus {
    color:${(props) => hoverColor(props, props.theme)};
    text-decoration:${(props) => (props.isInline ? 'underline' : 'none')}
    background-color:${(props) => focusBackground(props, props.theme)};
    border-bottom: 2px solid ${(props) => focusBorderBottom(props, props.theme)};
  }
  :disabled {
    cursor: not-allowed;
    color: ${(props) => props.theme.colors.darkGray};
    &:hover {
      text-decoration: none;
      color: ${(props) => props.theme.colors.darkGray};
      border-bottom: 2px solid ${(props) =>
        props.showUnderline && props.isActive 
          ? (props.$useSpruceColors ? props.theme.colors.spruceDarkGreen : props.theme.colors.primary) 
          : 'transparent'};
    }
  }
`;

export const StyledNavigationItemButton = styled.button<INavigationButtonProps>`
  ${sharedStyle};
  color: ${(props) => props.theme.colors.black};
`;
export const StyledNavigationItemButtonAsButton = styled.button<
  INavigationButtonProps & { appearance: 'primary' | 'default' | 'minimal' | 'outline'; intent: Intent }
>`
  ${sharedStyle};
  ${ButtonCss};
`;

export const StyledNavigationItemLink = styled.a<INavigationButtonProps>`
  ${sharedStyle};
`;

export const StyledNavigationItemLinkAsButton = styled.a<
  INavigationButtonProps & { appearance: 'primary' | 'default' | 'minimal' | 'outline'; intent: Intent }
>`
  ${sharedStyle};
  ${ButtonCss};
`;

const RouterLinkProxy: React.FC<INavigationButtonProps & LinkProps> = ({
  isActive,
  isInline,
  isPrimary,
  showUnderline,
  $useSpruceColors,
  ...rest
}) => <Link {...rest} />;

export const StyledRouterLink = styled(RouterLinkProxy)`
  ${sharedStyle};
  color: ${(props) => props.theme.colors.defaultContentText};
`;

export const StyledRouterLinkAsButton = styled(RouterLinkProxy)`
  ${sharedStyle};
  ${ButtonCss};
`;
